import { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, MapPin, Mail, ChevronRight } from "lucide-react";
import { SITE, NAV } from "@/lib/site";
import FloatingButtons from "@/components/FloatingButtons";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const useSmoothNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (to, onAfter) => (e) => {
    if (typeof to === "string" && to.includes("#")) {
      e.preventDefault();
      const [path, hash] = to.split("#");
      const targetPath = path || "/";
      if (location.pathname === targetPath) {
        scrollToId(hash);
      } else {
        navigate(targetPath, { state: { scrollTo: hash } });
      }
      if (onAfter) onAfter();
    }
  };
};

const Brand = ({ size = "md", showText = true }) => (
  <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
    <div className={`relative ${size === "lg" ? "h-14 w-14" : "h-11 w-11"} rounded-sm bg-[#0a0a0a] ring-1 ring-[#d4af37]/40 overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105`}>
      <img src={SITE.logo} alt="Kardeşler Yapı Logo" className="h-full w-full object-cover" loading="eager" />
    </div>
    {showText && (
      <div className="leading-none">
        <div className="font-display text-[18px] tracking-tight text-white">KARDEŞLER</div>
        <div className="font-display text-[11px] tracking-[0.42em] text-[#d4af37]">Y A P I</div>
      </div>
    )}
  </Link>
);

const TopBar = () => (
  <div className="hidden md:flex items-center justify-between border-b border-[#1a1a1a] bg-[#0a0a0a] text-xs text-white/55 px-6 py-2">
    <div className="flex items-center gap-5">
      <span className="inline-flex items-center gap-1.5">
        <MapPin className="h-3 w-3 text-[#d4af37]" /> {SITE.city}, {SITE.region}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Mail className="h-3 w-3 text-[#d4af37]" /> {SITE.email}
      </span>
    </div>
    <div className="flex items-center gap-4">
      {SITE.phones.map((p) => (
        <a key={p.value} href={`tel:${p.value}`} data-testid={`topbar-phone-${p.value}`} className="inline-flex items-center gap-1.5 hover:text-[#d4af37] transition">
          <Phone className="h-3 w-3 text-[#d4af37]" /> {p.label}
        </a>
      ))}
    </div>
  </div>
);

const Header = ({ onOpen }) => {
  const handleSmooth = useSmoothNav();
  return (
    <header className="sticky top-0 z-40 bg-[#121212]/85 backdrop-blur border-b border-[#1a1a1a]">
      <TopBar />
      <div className="flex items-center justify-between px-5 md:px-10 py-4">
        <Brand />
        <div className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={handleSmooth(n.to)}
              data-testid={`nav-${n.to.replace(/\//g, "-").replace(/#/g, "hash-") || "home"}`}
              className={({ isActive }) =>
                `relative font-display tracking-wide text-sm uppercase transition ${
                  isActive && !n.to.includes("#") ? "text-[#d4af37]" : "text-white/80 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`tel:${SITE.phones[0].value}`}
            data-testid="header-cta-call"
            className="hidden md:inline-flex items-center gap-2 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#121212] px-4 py-2 text-xs font-display uppercase tracking-[0.18em] transition"
          >
            <Phone className="h-3 w-3" /> Ücretsiz Keşif
          </a>
          <button
            onClick={onOpen}
            data-testid="hamburger-open"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#d4af37]/40 hover:border-[#d4af37] text-white"
          >
            <Menu className="h-4 w-4" />
            <span className="hidden sm:inline font-display text-xs tracking-[0.2em] uppercase">Menü</span>
          </button>
        </div>
      </div>
    </header>
  );
};

const MegaMenu = ({ open, onClose }) => {
  const handleSmooth = useSmoothNav();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);
  return (
    <div
      className={`fixed inset-0 z-[60] transition ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      data-testid="mega-menu"
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-[#0a0a0a]/95 backdrop-blur-md" onClick={onClose} />
      <div className={`absolute inset-0 grid grid-cols-1 lg:grid-cols-12 grain transition-all ${open ? "translate-y-0" : "-translate-y-2"}`}>
        <div className="lg:col-span-7 p-8 md:p-16 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <Brand size="lg" />
            <button
              onClick={onClose}
              data-testid="hamburger-close"
              className="h-12 w-12 inline-flex items-center justify-center border border-[#d4af37]/40 hover:border-[#d4af37] text-white hover:text-[#d4af37]"
              aria-label="Menüyü kapat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="mt-12 space-y-3">
            {NAV.map((n, i) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={(e) => { handleSmooth(n.to, onClose)(e); if (!n.to.includes("#")) onClose(); }}
                end={n.to === "/"}
                data-testid={`menu-link-${n.to.replace(/\//g, "-").replace(/#/g, "hash-") || "home"}`}
                className={({ isActive }) =>
                  `group flex items-center justify-between border-b border-white/8 py-5 transition ${isActive && !n.to.includes("#") ? "text-[#d4af37]" : "text-white hover:text-[#d4af37]"}`
                }
              >
                <span className="flex items-baseline gap-5">
                  <span className="font-mono text-[11px] text-white/40">0{i + 1}.</span>
                  <span className="font-display text-4xl md:text-6xl font-bold tracking-tighter">{n.label}</span>
                </span>
                <ChevronRight className="h-7 w-7 opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition" />
              </NavLink>
            ))}
          </nav>
          <div className="mt-10 flex flex-wrap items-center gap-4 text-xs text-white/55">
            <span className="font-mono uppercase tracking-[0.2em]">Eskişehir / TR-26</span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono">EST. {SITE.founded}</span>
          </div>
        </div>
        <div className="lg:col-span-5 relative bg-[#0a0a0a] border-l border-[#d4af37]/15 p-8 md:p-12 flex flex-col justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">İletişim</p>
            <div className="mt-6 space-y-4">
              {SITE.phones.map((p) => (
                <a key={p.value} href={`tel:${p.value}`} className="block group" data-testid={`menu-phone-${p.value}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Hat</div>
                  <div className="font-display text-2xl md:text-3xl text-white group-hover:text-[#d4af37] transition">{p.label}</div>
                </a>
              ))}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">E-Posta</div>
                <div className="font-display text-xl text-white">{SITE.email}</div>
              </div>
            </div>
          </div>
          <div>
            <div className="gold-divider mb-4" />
            <p className="font-display text-lg text-white/80 text-pretty">
              <span className="text-[#d4af37]">20 yılı aşkın</span> deneyimle Eskişehir&apos;in dört bir yanında
              <span className="text-white"> garantili yalıtım </span>çözümleri sunuyoruz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="relative bg-[#0a0a0a] border-t border-[#d4af37]/15 mt-24 grain">
    <div className="px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5 space-y-4">
        <Brand size="lg" />
        <p className="text-white/55 text-sm leading-relaxed max-w-md">
          Eskişehir merkezli, su / çatı / ısı yalıtımı ve çatı tadilat hizmetlerinde 20+ yıl deneyime sahip,
          garantili işçilik ve premium malzeme kullanan referansları güçlü bir aile şirketiyiz.
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
          {SITE.phones.map((p) => (
            <a
              key={p.value}
              href={`tel:${p.value}`}
              data-testid={`footer-phone-${p.value}`}
              className="inline-flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#121212] px-3 py-2 text-xs font-mono"
            >
              <Phone className="h-3 w-3" /> {p.label}
            </a>
          ))}
        </div>
      </div>
      <div className="md:col-span-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-4">Hızlı Erişim</p>
        <ul className="space-y-2 text-sm">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link to={n.to} className="text-white/60 hover:text-[#d4af37] transition">{n.label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37] mb-4">İletişim</p>
        <div className="space-y-3 text-sm text-white/65">
          <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0" /> {SITE.address}</p>
          <p className="flex items-start gap-2"><Mail className="h-4 w-4 text-[#d4af37] mt-0.5 shrink-0" /> {SITE.email}</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="inline-block mt-3 text-xs text-white/35 hover:text-[#d4af37] transition font-mono uppercase tracking-[0.25em]">
            Yönetici Girişi →
          </Link>
        </div>
      </div>
    </div>
    <div className="border-t border-white/5 px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-white/35 font-mono">
      <span>© {new Date().getFullYear()} KARDEŞLER YAPI · TÜM HAKLARI SAKLIDIR</span>
      <span>ESKİŞEHİR · TR-26 · 39.7767°N 30.5206°E</span>
    </div>
  </footer>
);

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const target = location.state?.scrollTo || (location.hash ? location.hash.replace(/^#/, "") : null);
    if (target) {
      let cancelled = false;
      const headerOffset = 96;
      const doScroll = (smooth) => {
        const el = document.getElementById(target);
        if (!el) return false;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
        return true;
      };
      doScroll(true);
      const delays = [200, 500, 900, 1400, 2000, 2700];
      const timers = delays.map((d, i) =>
        setTimeout(() => {
          if (cancelled) return;
          doScroll(i === delays.length - 1 ? false : true);
        }, d)
      );
      return () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      };
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, location.state]);
  return (
    <div className="min-h-screen bg-[#121212] text-white">
      <Header onOpen={() => setOpen(true)} />
      <MegaMenu open={open} onClose={() => setOpen(false)} />
      <main data-testid="public-main">
        <Outlet />
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}
